import { useState } from 'react';
import { t } from '../../i18n/index.js';
import { useSession } from '../../lib/session.js';
import { useApiData } from '../../lib/use-data.js';
import { formatDateTime, formatNumber } from '../../lib/format.js';
import { DataTable, type Column } from '../../patterns/DataTable.js';
import { PageHeader } from '../../patterns/PageHeader.js';
import { Badge } from '../../ui/Badge.js';
import type { AuditEntry } from '../../lib/api.js';

/**
 * What administrators have done, and to whom.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * PAGED BY THE PLATFORM, NOT BY THE TABLE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Every other table in this console loads its rows and pages them in the
 * browser, which is right for six accounts and one roster. This one grows
 * without limit — every sign-in, every sign-out, every purge, forever — so
 * paging it client-side would mean fetching the whole trail to show fifty
 * rows of it, and getting slower every week.
 *
 * So the offset lives here and travels to the endpoint, which caps the page
 * at 200 whatever is asked for. `total` comes back from a window function
 * over the whole filtered set, so "50 of 3,412" is the real total rather than
 * a count of what was handed over.
 *
 * ── THERE IS NO WRITE PATH, AND THERE CANNOT BE ───────────────────────────
 *
 * No grant anywhere permits UPDATE or DELETE on `admin_audit`. Entries are
 * made only as a side effect of the acts they record, which is what makes
 * this readable as evidence rather than as a log somebody maintains.
 */
const PAGE_SIZE = 50;

export function AuditPage() {
  const { api } = useSession();
  const [page, setPage] = useState(0);
  const { data, loading, error, reload } = useApiData(
    () => api.audit({ limit: PAGE_SIZE, offset: page * PAGE_SIZE }),
    [api, page],
  );

  const columns: Column<AuditEntry>[] = [
    {
      id: 'when',
      header: t('audit.when'),
      nowrap: true,
      cell: (e) => formatDateTime(e.createdAt),
    },
    {
      id: 'actor',
      header: t('audit.actor'),
      cell: (e) => <span className="font-medium text-primary">{e.actor}</span>,
    },
    {
      id: 'action',
      header: t('audit.action'),
      cell: (e) => <span className="text-secondary">{e.action}</span>,
    },
    {
      id: 'subject',
      header: t('audit.subject'),
      /*
        The slug is DENORMALISED into the audit row on purpose.

        Joining to `tenants` for the name would mean the trail stopped being
        able to describe an account after it was renamed — and would say
        nothing at all about one that no longer exists, which is precisely the
        case a purge record is written for.
      */
      cell: (e) =>
        e.subjectSlug ? (
          <span className="text-primary">{e.subjectSlug}</span>
        ) : (
          <span className="text-tertiary">{t('audit.noSubject')}</span>
        ),
    },
    {
      id: 'status',
      header: t('audit.status'),
      cell: (e) => (
        <Badge intent={e.status === 'success' ? 'success' : 'danger'}>{e.status}</Badge>
      ),
    },
  ];

  const pageCount = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <>
      <PageHeader title={t('audit.title')} description={t('audit.description')} />
      <DataTable
        columns={columns}
        rows={data?.entries ?? []}
        rowKey={(e) => e.id}
        loading={loading}
        error={error}
        onRetry={reload}
        /*
          An empty trail on page one means nothing has happened yet, which is
          only true of a brand new platform — the bootstrap admin's first
          sign-in writes a row. On a later page it means the offset is past
          the end, which is a different sentence and not one worth a screen:
          the pager below cannot produce it.
        */
        emptyReason="nothing-yet"
        emptyTitle={t('audit.emptyTitle')}
        emptyDescription={t('audit.emptyBody')}
        pagination={{
          page,
          pageCount,
          onPage: setPage,
          totalItems: data?.total ?? 0,
          itemNoun: t('audit.noun'),
        }}
        footer={
          data ? (
            <p className="text-sm text-secondary">
              {t('audit.total', { total: formatNumber(data.total) })}
            </p>
          ) : null
        }
      />
    </>
  );
}
